import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { useAccount } from "@/context/AccountAuthContext";
import { IMG_PALM_BEACH } from "@/lib/assets";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, CalendarDays, Users, Clock, Check, Sparkles, Plus, Trash2, PartyPopper, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TXT = {
  fr: {
    kicker: "Programmes spéciaux",
    title: "Événements & forfaits",
    subtitle: "Des aventures immersives sur plusieurs jours, en dehors du calendrier habituel.",
    empty: "Aucun événement programmé pour le moment. Revenez bientôt !",
    included: "Ce qui est inclus",
    fullPackage: "Forfait complet obligatoire",
    book: "Réserver le forfait",
    member: "Adhérent",
    nonMember: "Non-adhérent",
    perChild: "/ enfant",
    dialogTitle: "Réservation du forfait",
    parentInfo: "Vos coordonnées",
    parentName: "Nom du responsable",
    email: "E-mail",
    phone: "Téléphone",
    childrenTitle: "Enfant(s) inscrit(s)",
    firstName: "Prénom",
    lastName: "Nom",
    dob: "Date de naissance",
    allergies: "Allergies / infos médicales",
    addChild: "Ajouter un enfant",
    emergency: "Contact d'urgence",
    consentsTitle: "Autorisations",
    resident: "Je confirme que l'enfant réside à Saint-Barthélemy.",
    consentImage: "J'autorise la prise et la diffusion de photos/vidéos.",
    consentTransport: "J'autorise le transport en cas d'urgence.",
    total: "Total",
    pay: "Payer et réserver",
    required: "Merci de renseigner votre nom, un e-mail valide et le prénom de chaque enfant.",
    residentReq: "Les activités sont réservées aux résidents de Saint-Barthélemy.",
    processing: "Vérification du paiement…",
    success: "Réservation confirmée !",
    successText: "Votre forfait est réservé. Un e-mail de confirmation vous sera envoyé.",
    pending: "Paiement en cours de traitement. Vous recevrez une confirmation sous peu.",
    close: "Fermer",
  },
  en: {
    kicker: "Special programs",
    title: "Events & packages",
    subtitle: "Immersive multi-day adventures, outside the regular calendar.",
    empty: "No events scheduled yet. Check back soon!",
    included: "What's included",
    fullPackage: "Full package required",
    book: "Book the package",
    member: "Member",
    nonMember: "Non-member",
    perChild: "/ child",
    dialogTitle: "Package booking",
    parentInfo: "Your details",
    parentName: "Guardian name",
    email: "Email",
    phone: "Phone",
    childrenTitle: "Enrolled child(ren)",
    firstName: "First name",
    lastName: "Last name",
    dob: "Date of birth",
    allergies: "Allergies / medical info",
    addChild: "Add a child",
    emergency: "Emergency contact",
    consentsTitle: "Authorizations",
    resident: "I confirm the child resides in Saint-Barthélemy.",
    consentImage: "I allow photos/videos to be taken and shared.",
    consentTransport: "I allow emergency transport.",
    total: "Total",
    pay: "Pay & book",
    required: "Please provide your name, a valid email and each child's first name.",
    residentReq: "Activities are reserved for Saint-Barthélemy residents.",
    processing: "Verifying payment…",
    success: "Booking confirmed!",
    successText: "Your package is booked. A confirmation email will be sent to you.",
    pending: "Payment is being processed. You'll receive a confirmation shortly.",
    close: "Close",
  },
};

let seq = 1;
const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export default function SpecialEvents() {
  const { lang } = useLang();
  const account = useAccount();
  const x = TXT[lang];
  const [events, setEvents] = useState(null);
  const [payState, setPayState] = useState(null);

  const [openEvent, setOpenEvent] = useState(null);
  const [parent, setParent] = useState({ name: "", email: "", phone: "" });
  const [children, setChildren] = useState([{ id: seq++, first_name: "", last_name: "", dob: "", allergies: "" }]);
  const [emergency, setEmergency] = useState({ name: "", phone: "" });
  const [consents, setConsents] = useState({ resident: false, image_rights: false, emergency_transport: false });
  const [isMember, setIsMember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/special-events`).then((r) => setEvents(r.data.events || [])).catch(() => setEvents([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (account?.user) {
      setParent((p) => ({ name: p.name || account.user.name || "", email: p.email || account.user.email || "", phone: p.phone || account.user.phone || "" }));
    }
  }, [account?.user]);

  // member check
  useEffect(() => {
    if (!validEmail(parent.email)) { setIsMember(false); return; }
    const t = setTimeout(async () => {
      try { const r = await axios.get(`${API}/member-status`, { params: { email: parent.email.trim() } }); setIsMember(!!r.data.is_member); }
      catch { setIsMember(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [parent.email]);

  // payment return polling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPayState("processing");
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setPayState("success"); return; }
        if (res.data.status === "expired") { setPayState("failed"); return; }
      } catch (e) { /* retry */ }
      attempts += 1;
      if (attempts >= 10) { setPayState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  const priceFor = (e, member) => {
    const base = member ? e.member_price : e.non_member_price;
    const promo = member ? e.promo_member_price : e.promo_non_member_price;
    return { base, promo: promo != null ? promo : null, final: promo != null ? promo : base };
  };

  const openBooking = (e) => {
    setOpenEvent(e);
    setError("");
    setChildren([{ id: seq++, first_name: "", last_name: "", dob: "", allergies: "" }]);
    if (!account?.user) { setParent({ name: "", email: "", phone: "" }); }
    setConsents({ resident: false, image_rights: false, emergency_transport: false });
  };

  const addChild = () => setChildren((c) => [...c, { id: seq++, first_name: "", last_name: "", dob: "", allergies: "" }]);
  const removeChild = (id) => setChildren((c) => (c.length === 1 ? c : c.filter((k) => k.id !== id)));
  const updChild = (id, k, v) => setChildren((c) => c.map((k2) => (k2.id === id ? { ...k2, [k]: v } : k2)));

  const pay = async () => {
    setError("");
    if (!parent.name.trim() || !validEmail(parent.email) || children.some((c) => !c.first_name.trim())) { setError(x.required); return; }
    if (!consents.resident) { setError(x.residentReq); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/special-events/${openEvent.id}/checkout`, {
        parent: { name: parent.name.trim(), email: parent.email.trim(), phone: parent.phone.trim() },
        children: children.map((c) => ({ first_name: c.first_name.trim(), last_name: c.last_name.trim(), dob: c.dob, allergies: c.allergies.trim() })),
        origin_url: window.location.origin,
        emergency_name: emergency.name.trim(),
        emergency_phone: emergency.phone.trim(),
        consents: { resident: consents.resident, image_rights: consents.image_rights, emergency_transport: consents.emergency_transport },
      });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur");
      setLoading(false);
    }
  };

  const totalAmount = openEvent ? priceFor(openEvent, isMember).final * children.length : 0;

  if (payState === "success" || payState === "pending" || payState === "processing") {
    return (
      <div className="min-h-screen bg-[#fbf6ec] pt-28 pb-20 px-5 flex items-center justify-center">
        <div className="max-w-lg text-center bg-white rounded-3xl border border-[#6f7d54]/10 p-10" data-testid="event-pay-result">
          {payState === "processing" ? (
            <><Loader2 size={48} className="animate-spin text-[#e07a5f] mx-auto mb-4" /><p className="font-body text-[#3a4d68]">{x.processing}</p></>
          ) : payState === "success" ? (
            <><PartyPopper size={52} className="text-[#5e7d52] mx-auto mb-4" /><h1 className="font-head text-3xl text-[#6f7d54] mb-2">{x.success}</h1><p className="font-body text-[#3a4d68]">{x.successText}</p></>
          ) : (
            <><Clock size={52} className="text-[#8a6a1f] mx-auto mb-4" /><p className="font-body text-[#3a4d68]">{x.pending}</p></>
          )}
          <a href="/evenements" className="inline-block mt-6 rounded-full bg-[#6f7d54] text-white font-bold text-sm px-6 py-3">{x.close}</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbf6ec]" data-testid="special-events-page">
      {/* Hero */}
      <div className="relative pt-32 pb-16 px-5 overflow-hidden">
        <div className="absolute inset-0 bg-[#6f7d54]" />
        <img src={IMG_PALM_BEACH} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e0b36a]/25 text-[#f7efe0] font-bold text-xs px-3 py-1 mb-4"><Sparkles size={13} /> {x.kicker}</span>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl text-white mb-4">{x.title}</h1>
          <p className="font-body text-[#f7efe0]/85 text-base md:text-lg max-w-2xl mx-auto">{x.subtitle}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-14">
        {events === null ? (
          <div className="flex justify-center py-20"><Loader2 size={40} className="animate-spin text-[#e07a5f]" /></div>
        ) : events.length === 0 ? (
          <p className="text-center font-body text-[#3a4d68]/70 py-20" data-testid="events-empty">{x.empty}</p>
        ) : (
          <div className="space-y-8">
            {events.map((e) => {
              const title = lang === "fr" ? e.title_fr : (e.title_en || e.title_fr);
              const subtitle = lang === "fr" ? e.subtitle_fr : (e.subtitle_en || e.subtitle_fr);
              const ageGroup = lang === "fr" ? e.age_group_fr : (e.age_group_en || e.age_group_fr);
              const datesLabel = lang === "fr" ? e.dates_label_fr : (e.dates_label_en || e.dates_label_fr);
              const schedule = lang === "fr" ? e.schedule_fr : (e.schedule_en || e.schedule_fr);
              const promoLabel = lang === "fr" ? e.promo_label_fr : (e.promo_label_en || e.promo_label_fr);
              const included = (lang === "fr" ? e.included_fr : (e.included_en || e.included_fr) || "").split("\n").map((s) => s.trim()).filter(Boolean);
              const mp = priceFor(e, true), nmp = priceFor(e, false);
              return (
                <div key={e.id} data-testid={`event-card-${e.id}`} className="bg-white rounded-3xl border border-[#6f7d54]/10 overflow-hidden shadow-sm">
                  {e.image && (
                    <div className="w-full h-52 md:h-64 overflow-hidden bg-[#6f7d54]/5">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${e.image}`} alt={title} data-testid={`event-image-${e.id}`} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="grid md:grid-cols-[1.4fr_1fr]">
                    <div className="p-7 md:p-9">
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        {ageGroup && <span className="rounded-full bg-[#2e8b9e]/12 text-[#2e8b9e] font-bold text-xs px-3 py-1">{ageGroup}</span>}
                        {promoLabel && (mp.promo != null || nmp.promo != null) && <span className="rounded-full bg-[#e07a5f] text-white font-bold text-xs px-3 py-1">{promoLabel}</span>}
                      </div>
                      <h2 className="font-head text-2xl md:text-3xl text-[#6f7d54] leading-tight">{title}</h2>
                      {subtitle && <p className="font-body text-[#3a4d68] mt-2">{subtitle}</p>}
                      <div className="flex flex-col gap-2 mt-5 text-sm font-body text-[#3a4d68]">
                        {datesLabel && <span className="inline-flex items-center gap-2"><CalendarDays size={16} className="text-[#e07a5f]" /> {datesLabel}</span>}
                        {schedule && <span className="inline-flex items-center gap-2"><Clock size={16} className="text-[#e07a5f]" /> {schedule}</span>}
                        <span className="inline-flex items-center gap-2"><Users size={16} className="text-[#e07a5f]" /> {e.capacity} places{e.full_package ? ` · ${x.fullPackage}` : ""}</span>
                      </div>
                      {included.length > 0 && (
                        <div className="mt-6">
                          <p className="font-head font-bold text-[#6f7d54] mb-2">{x.included}</p>
                          <ul className="space-y-1.5">
                            {included.map((it, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm font-body text-[#3a4d68]"><Check size={15} className="text-[#5e7d52] mt-0.5 shrink-0" /> {it}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="bg-[#fbf6ec] p-7 md:p-9 flex flex-col justify-center border-t md:border-t-0 md:border-l border-[#6f7d54]/10">
                      <div className="space-y-4">
                        <PriceRow label={x.member} p={mp} />
                        <PriceRow label={x.nonMember} p={nmp} />
                      </div>
                      <button onClick={() => openBooking(e)} data-testid={`event-book-${e.id}`} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-sm px-6 py-3.5 transition-colors">
                        {x.book}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Booking dialog */}
      <Dialog open={!!openEvent} onOpenChange={(o) => !o && setOpenEvent(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="event-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-[#6f7d54]">{x.dialogTitle}{openEvent ? ` — ${lang === "fr" ? openEvent.title_fr : (openEvent.title_en || openEvent.title_fr)}` : ""}</DialogTitle>
            <DialogDescription className="sr-only">{x.dialogTitle}</DialogDescription>
          </DialogHeader>
          {openEvent && (
            <div className="space-y-5">
              <div>
                <p className="font-head font-bold text-[#6f7d54] mb-2">{x.parentInfo}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input label={x.parentName} value={parent.name} onChange={(v) => setParent({ ...parent, name: v })} testid="ev-parent-name" />
                  <Input label={x.email} value={parent.email} onChange={(v) => setParent({ ...parent, email: v })} testid="ev-parent-email" />
                  <Input label={x.phone} value={parent.phone} onChange={(v) => setParent({ ...parent, phone: v })} testid="ev-parent-phone" />
                </div>
                {isMember && <p className="text-xs font-bold text-[#4a6742] mt-1.5">✓ {x.member}</p>}
              </div>

              <div>
                <p className="font-head font-bold text-[#6f7d54] mb-2">{x.childrenTitle}</p>
                <div className="space-y-3">
                  {children.map((c, i) => (
                    <div key={c.id} className="bg-[#fbf6ec] rounded-xl p-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Input label={x.firstName} value={c.first_name} onChange={(v) => updChild(c.id, "first_name", v)} testid={`ev-child-fn-${i}`} />
                        <Input label={x.lastName} value={c.last_name} onChange={(v) => updChild(c.id, "last_name", v)} />
                        <Input label={x.dob} type="date" value={c.dob} onChange={(v) => updChild(c.id, "dob", v)} />
                        <Input label={x.allergies} value={c.allergies} onChange={(v) => updChild(c.id, "allergies", v)} />
                      </div>
                      {children.length > 1 && <button onClick={() => removeChild(c.id)} className="text-[#e63946] text-xs font-bold mt-2 inline-flex items-center gap-1"><Trash2 size={13} /> Retirer</button>}
                    </div>
                  ))}
                </div>
                <button onClick={addChild} data-testid="ev-add-child" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#2e8b9e]/12 text-[#2e8b9e] font-bold text-sm px-4 py-2"><Plus size={15} /> {x.addChild}</button>
              </div>

              <div>
                <p className="font-head font-bold text-[#6f7d54] mb-2">{x.emergency}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input label={x.parentName} value={emergency.name} onChange={(v) => setEmergency({ ...emergency, name: v })} />
                  <Input label={x.phone} value={emergency.phone} onChange={(v) => setEmergency({ ...emergency, phone: v })} />
                </div>
              </div>

              <div>
                <p className="font-head font-bold text-[#6f7d54] mb-2">{x.consentsTitle}</p>
                <div className="space-y-2 text-sm font-body text-[#3a4d68]">
                  <Check2 checked={consents.resident} onChange={(v) => setConsents({ ...consents, resident: v })} testid="ev-consent-resident" label={x.resident} required />
                  <Check2 checked={consents.image_rights} onChange={(v) => setConsents({ ...consents, image_rights: v })} label={x.consentImage} />
                  <Check2 checked={consents.emergency_transport} onChange={(v) => setConsents({ ...consents, emergency_transport: v })} label={x.consentTransport} />
                </div>
              </div>

              {error && <p className="text-[#b5232f] text-sm font-semibold flex items-center gap-1.5"><AlertTriangle size={15} /> {error}</p>}

              <div className="flex items-center justify-between border-t border-[#6f7d54]/10 pt-4">
                <div className="font-head text-lg text-[#6f7d54]">{x.total} : <b>{totalAmount}€</b></div>
                <button onClick={pay} disabled={loading} data-testid="ev-pay" className="inline-flex items-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-sm px-6 py-3 disabled:opacity-50 transition-colors">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {x.pay}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PriceRow({ label, p }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="font-body text-sm text-[#3a4d68]">{label}</span>
      <span className="font-head text-lg text-[#6f7d54]">
        {p.promo != null ? (
          <><s className="text-[#3a4d68]/45 text-base mr-2">{p.base}€</s><b className="text-[#cf6a50]">{p.promo}€</b></>
        ) : (
          <b>{p.base}€</b>
        )}
      </span>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="text-sm block">
      <span className="block font-semibold text-[#3a4d68] mb-1">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2 bg-white" />
    </label>
  );
}

function Check2({ checked, onChange, label, testid, required }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-1" />
      <span>{label}{required ? <span className="text-[#e07a5f]"> *</span> : null}</span>
    </label>
  );
}
